import * as React from "react";

import {
  floatingControlHeightClassName,
  floatingControlMultilineLayoutClassName,
  floatingControlSingleLineLayoutClassName,
} from "@/components/ui/form-control-styles";
import { floatingLabelClassName } from "@/components/ui/form-label-styles";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type FloatingFieldContextValue = { floating: boolean; multiline: boolean };

const NOT_FLOATING: FloatingFieldContextValue = { floating: false, multiline: false };

const FloatingFieldContext = React.createContext<FloatingFieldContextValue>(NOT_FLOATING);

/** True only inside a FloatingField (i.e. a labeled form field, not a filter bar). */
export function useFloatingField(): FloatingFieldContextValue {
  return React.useContext(FloatingFieldContext);
}

/** Returns the size/padding classes a control should use, or "" outside a FloatingField. */
export function useFloatingControlClassName(): string {
  const { floating, multiline } = useFloatingField();
  if (!floating) return "";
  if (multiline) return floatingControlMultilineLayoutClassName;
  return cn(floatingControlHeightClassName, floatingControlSingleLineLayoutClassName);
}

export function FloatingField({
  filled = false,
  multiline = false,
  alwaysFloat = false,
  showIdlePlaceholder = false,
  className,
  children,
}: {
  filled?: boolean;
  multiline?: boolean;
  /**
   * Keeps the label floated even while empty — for controls with a start adornment or a value
   * that is never blank (currency prefix, color swatch, native time input).
   */
  alwaysFloat?: boolean;
  /** Keep `input::placeholder` visible while idle (label is already floated). */
  showIdlePlaceholder?: boolean;
  className?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const value = React.useMemo(() => ({ floating: true, multiline }), [multiline]);
  return (
    <FloatingFieldContext.Provider value={value}>
      <div
        data-slot="floating-field"
        data-filled={filled || alwaysFloat ? "true" : "false"}
        data-multiline={multiline ? "true" : undefined}
        data-show-idle-placeholder={showIdlePlaceholder ? "true" : undefined}
        className={cn("floating-field relative w-full min-w-0", className)}
      >
        {children}
      </div>
    </FloatingFieldContext.Provider>
  );
}

/**
 * Floating label for controls with no react-hook-form context (filters, popover time inputs).
 * Inside a form, use `FormLabel`, which adds the error state and the `(optional)` suffix.
 */
export function FloatingLabel({
  htmlFor,
  required = false,
  className,
  children,
}: {
  htmlFor: string;
  required?: boolean;
  className?: string;
  children: string;
}): React.JSX.Element {
  return (
    <Label htmlFor={htmlFor} className={cn(floatingLabelClassName, className)} title={children}>
      <span className="min-w-0 truncate">{children}</span>
      {required ? (
        <span className="shrink-0" aria-hidden="true">
          *
        </span>
      ) : null}
    </Label>
  );
}

/**
 * Clears the floating-field context for overlay content.
 *
 * React context flows through portals, so a control rendered inside a picker's popover
 * (e.g. the time input in the date picker) would otherwise inherit the 56px sizing meant
 * for the trigger that opened it. Overlay content is its own layout scope; anything in
 * there that wants a floating label opts in with its own {@link FloatingField}.
 */
export function FloatingFieldReset({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <FloatingFieldContext.Provider value={NOT_FLOATING}>{children}</FloatingFieldContext.Provider>
  );
}

/** Empty-value predicate shared by every field type. */
export function hasFieldValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "number") return true; // 0 is a real value
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  return true;
}
